/** Billing rail from country. Trinidad → TTD/Wam. Rest of world → USD/PayPal. */

export const BILLING_COUNTRY_OPTIONS = [
  { value: "Trinidad and Tobago", label: "Trinidad and Tobago" },
  { value: "United States", label: "United States" },
  { value: "United Kingdom", label: "United Kingdom" },
  { value: "Canada", label: "Canada" },
  { value: "Jamaica", label: "Jamaica" },
  { value: "Barbados", label: "Barbados" },
  { value: "Guyana", label: "Guyana" },
  { value: "Other", label: "Other" },
] as const;

export type BillingCurrency = "TTD" | "USD";
export type BillingRail = "wam" | "paypal";

export function isTrinidadCountry(country?: string | null): boolean {
  const c = (country ?? "").trim().toLowerCase();
  if (!c) return true;
  return /trinidad|tobago|\btt\b/.test(c);
}

export function billingCurrencyFromCountry(
  country?: string | null,
): BillingCurrency {
  return isTrinidadCountry(country) ? "TTD" : "USD";
}

export function billingRailFromCountry(country?: string | null): BillingRail {
  return isTrinidadCountry(country) ? "wam" : "paypal";
}

export function usdCentsFromTtdCents(
  ttdCents: number,
  fxUsdToTtd: number,
): number {
  if (!Number.isFinite(ttdCents) || ttdCents < 1) {
    throw new Error("Invalid TTD amount");
  }
  if (!Number.isFinite(fxUsdToTtd) || fxUsdToTtd < 1 || fxUsdToTtd > 50) {
    throw new Error("Live FX rate is unavailable. Try again.");
  }
  return usdWholeDollarCents(ttdCents, fxUsdToTtd);
}

/**
 * Prices in USD are whole dollars: nearest dollar of TTD / rate, at least US$1.
 * One helper for what checkout shows and what PayPal charges.
 */
export function usdWholeDollarCents(ttdCents: number, fxUsdToTtd: number): number {
  if (!Number.isFinite(ttdCents) || ttdCents <= 0) return 0;
  if (!Number.isFinite(fxUsdToTtd) || fxUsdToTtd < 1) return 0;
  return Math.max(100, Math.round(ttdCents / fxUsdToTtd / 100) * 100);
}

/** Display conversion. Zero is allowed; never throws for empty balances. */
export function displayUsdCentsFromTtd(
  ttdCents: number,
  fxUsdToTtd: number,
): number {
  if (!Number.isFinite(ttdCents) || ttdCents <= 0) return 0;
  if (!Number.isFinite(fxUsdToTtd) || fxUsdToTtd < 1) return 0;
  return Math.max(0, Math.round(ttdCents / fxUsdToTtd));
}

export function usdCentsToTtdCents(
  usdCents: number,
  fxUsdToTtd: number,
): number {
  if (!Number.isFinite(usdCents) || usdCents < 0) {
    throw new Error("Invalid USD amount");
  }
  if (!Number.isFinite(fxUsdToTtd) || fxUsdToTtd < 1 || fxUsdToTtd > 50) {
    throw new Error("Live FX rate is unavailable. Try again.");
  }
  return Math.max(0, Math.round(usdCents * fxUsdToTtd));
}

export function billingHintFromCountry(country?: string | null): string {
  return isTrinidadCountry(country)
    ? "Trinidad pays in TTD with Wam."
    : "This country pays in USD with PayPal.";
}
